/**
 * SessionDebugPanel — Session state panel in header (DEC-050)
 *
 * Shows session info: ID, stage, energy, message count, duration, therapist.
 * Only visible when dev mode is enabled.
 */

import { useMemo } from 'react'
import { useChatStore } from '../../stores/chatStore'

interface SessionDebugInfo {
  session_id?: number
  stage?: string
  energy?: number
  therapist_model?: string
}

interface SessionDebugPanelProps {
  sessionDebugInfo?: SessionDebugInfo | null
}

export default function SessionDebugPanel({ sessionDebugInfo }: SessionDebugPanelProps) {
  const sessionId = useChatStore((s) => s.sessionId)
  const messages = useChatStore((s) => s.messages)
  const currentTherapist = useChatStore((s) => s.currentTherapist)

  // Calculate duration from messages
  const durationMinutes = useMemo(() => {
    if (messages.length === 0) return 0
    const firstMsg = messages[0]
    const firstTime = new Date(firstMsg.createdAt).getTime()
    const now = Date.now()
    return Math.floor((now - firstTime) / 60000)
  }, [messages])

  // Build display parts
  const parts: string[] = []

  // Session ID
  const displaySessionId = sessionDebugInfo?.session_id ?? sessionId
  if (displaySessionId) {
    parts.push(`#${displaySessionId}`)
  }

  // Stage (from backend if available)
  if (sessionDebugInfo?.stage) {
    parts.push(sessionDebugInfo.stage)
  }

  // Energy (from backend if available)
  if (sessionDebugInfo?.energy != null) {
    const energyPercent = Math.round(sessionDebugInfo.energy * 100)
    parts.push(`⚡${energyPercent}%`)
  }

  // Message count
  parts.push(`💬${messages.length}`)

  // Duration
  if (durationMinutes > 0) {
    parts.push(`⏱️${durationMinutes}m`)
  }

  // Therapist
  if (currentTherapist?.type) {
    const therapistDisplay = currentTherapist.type
    const model = sessionDebugInfo?.therapist_model || 'sonnet'
    parts.push(`${therapistDisplay}/${model}`)
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-neutral-100 rounded-lg text-[10px] text-neutral-600 font-mono" data-testid="session-debug-panel">
      <span className="text-neutral-400">📊</span>
      {parts.map((part, i) => (
        <span key={i} className="flex items-center">
          {i > 0 && <span className="text-neutral-300 mx-1">|</span>}
          {part}
        </span>
      ))}
    </div>
  )
}
